import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ArrowLeft,
  Search,
  Plus,
  Trash2,
  ArrowLeftRight,
  CheckCircle2,
  XCircle,
  Save,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import type { Engagement, Entity, JournalEntryHeader, JournalEntryLine, TBAccount } from '../types';
import {
  getAccounts,
  getJournalEntryLines,
  saveMAdjLines,
  updateSignoffState,
  acknowledgePrincipalReview,
} from '../db/database';
import { getFSRef } from '../utils/mappings';
import { centsToDollars, displayAmountRounded } from '../utils/format';
import {
  isTierSigned,
  isTierClickable,
  computeNextSignoffState,
  getSignoffTitle,
  hasCautionFlag,
} from '../utils/signoff';
import type { SignoffTier } from '../utils/signoff';
import { Button } from './Button';
import { SignoffCheckbox } from './SignoffCheckbox';
import { CurrencyInput } from './CurrencyInput';
import AccountModal from './AccountModal';

// ============================================================================
// TYPES
// ============================================================================

interface DraftLine {
  key: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  debitCents: number;
  creditCents: number;
  isInvalidAccount?: boolean;
}

interface MAdjustmentEditorProps {
  header: JournalEntryHeader;
  engagement: Engagement;
  entities: Entity[];
  onBack: (reload: boolean) => void;
}

type TBSortField = 'accountNumber' | 'accountName' | 'grouping' | 'subGrouping';

// ============================================================================
// HELPERS
// ============================================================================

function TbSortIndicator({ field, current, direction }: {
  field: TBSortField;
  current: TBSortField;
  direction: 'asc' | 'desc';
}) {
  if (field !== current) return null;
  return direction === 'asc'
    ? <ChevronUp className="inline w-3 h-3 ml-0.5 shrink-0" />
    : <ChevronDown className="inline w-3 h-3 ml-0.5 shrink-0" />;
}

function centsToInputValue(cents: number): string {
  if (cents === 0) return '-';
  return String(Math.round(centsToDollars(cents)));
}

function inputValueToCents(raw: string): number {
  const n = parseInt(raw);
  if (isNaN(n) || n < 0) return 0;
  return n * 100;
}

// ============================================================================
// M-ADJUSTMENT EDITOR
// ============================================================================

export default function MAdjustmentEditor({ header, engagement, entities, onBack }: MAdjustmentEditorProps) {
  const [accounts, setAccounts] = useState<TBAccount[]>([]);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tbSearch, setTbSearch] = useState('');
  const [jeNumber, setJeNumber] = useState(header.jeNumber);
  const [jeName, setJeName] = useState(header.jeName);
  const [localHeader, setLocalHeader] = useState<JournalEntryHeader>(header);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [tbSortField, setTbSortField] = useState<TBSortField>('accountNumber');
  const [tbSortDirection, setTbSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedTbAccountIds, setSelectedTbAccountIds] = useState<Set<string>>(new Set());
  const [flashingTbAccountId, setFlashingTbAccountId] = useState<string | null>(null);
  const [notesText, setNotesText] = useState(header.notes ?? '');
  const [isNotesExpanded, setIsNotesExpanded] = useState(true);
  const [focusedInputKey, setFocusedInputKey] = useState<string | null>(null);
  const [differenceType, setDifferenceType] = useState<'Permanent' | 'Temporary'>(
    header.differenceType ?? 'Permanent'
  );
  const [calcInputs, setCalcInputs] = useState<Record<string, number>>(header.calcInputs ?? {});

  const setCalcInput = (key: string, value: number) =>
    setCalcInputs((prev) => ({ ...prev, [key]: value }));

  const lastTbClickedIdRef = useRef<string | null>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Data loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!engagement.id || !header.id) return;
      setIsLoading(true);
      try {
        const [accs, existingLines] = await Promise.all([
          getAccounts(engagement.id!),
          getJournalEntryLines(header.id!),
        ]);

        if (cancelled) return;

        setAccounts(accs);

        const accMap = new Map(accs.map((a) => [a.id!, a]));
        const draft: DraftLine[] = existingLines.map((l: JournalEntryLine) => {
          const acc = accMap.get(l.accountId);
          const fsRef = getFSRef(acc?.grouping ?? '');
          const isInvalidAccount = !acc || (fsRef !== 'Income' && fsRef !== 'Deductions');
          return {
            key: crypto.randomUUID(),
            accountId: l.accountId,
            accountNumber: acc?.accountNumber ?? '—',
            accountName: acc?.accountName ?? '(deleted account)',
            debitCents:  l.amount > 0 ? l.amount : 0,
            creditCents: l.amount < 0 ? Math.abs(l.amount) : 0,
            isInvalidAccount,
          };
        });
        setLines(draft);
      } catch (err) {
        console.error('MAdjustmentEditor load failed:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [engagement.id, header.id]);

  // ── Auto-Pull aggregates ───────────────────────────────────────────────────

  const taggedAccounts = useMemo(
    () => accounts.filter(a => a.mAdjRef === localHeader.jeNumber),
    [accounts, localHeader.jeNumber]
  );

  const autoCYBalance = useMemo(
    () => taggedAccounts.reduce((sum, a) => sum + (a.adjBook ?? 0), 0),
    [taggedAccounts]
  );

  const autoPYBalance = useMemo(
    () => taggedAccounts.reduce((sum, a) => sum + (a.pyAdjBook ?? 0), 0),
    [taggedAccounts]
  );

  // ── Computed net adjustment ───────────────────────────────────────────────

  const derivedNetAdj = useMemo(() => {
    const override = calcInputs.masterOverride || 0;
    switch (localHeader.calcType) {
      // ── New calcTypes ────────────────────────────────────────────────────
      case 'IncomeStatement':
        if (override !== 0) return override;
        return autoCYBalance + (calcInputs.taxAmount || 0);

      case 'BS_Liability':
      case 'BS_Asset':
        if (override !== 0) return override;
        return autoPYBalance - autoCYBalance;

      case 'BS_Liability_Payout':
      case 'BS_Asset_Payout':
        if (override !== 0) return override;
        return (autoPYBalance + (calcInputs.pyPayout || 0))
             - (autoCYBalance + (calcInputs.cyPayout || 0));

      // ── Legacy (kept for existing saved data) ───────────────────────────
      case 'Meals50': {
        const fiftyPercent = Math.round(autoCYBalance * -0.5);
        return autoCYBalance + fiftyPercent + override;
      }
      case 'Rollforward':
        return (autoPYBalance + (calcInputs.pyPayout || 0)) - (autoCYBalance + (calcInputs.cyPayout || 0)) + override;
      case 'BookTaxDiff':
        return autoCYBalance + (calcInputs.taxAmount || 0) + override;
      case 'Standard':
        return autoCYBalance + override;
      case 'Manual':
      default:
        return calcInputs.manualAmount || 0;
    }
  }, [localHeader.calcType, autoCYBalance, autoPYBalance, calcInputs]);

  // ── Balance values ────────────────────────────────────────────────────────

  const totalDebits = useMemo(
    () => lines.reduce((s, l) => s + l.debitCents, 0),
    [lines]
  );
  const totalCredits = useMemo(
    () => lines.reduce((s, l) => s + l.creditCents, 0),
    [lines]
  );

  // Reconciliation balance rule: net of lines must equal the NEGATION of the declared Net Adjustment.
  // Favorable (negative derivedNetAdj) → requires net Debit lines (debits > credits).
  // Unfavorable (positive derivedNetAdj) → requires net Credit lines (credits > debits).
  const netOfLines = totalDebits - totalCredits;
  const isBalanced = (totalDebits > 0 || totalCredits > 0) && netOfLines === -derivedNetAdj;
  const balanceDiff = -derivedNetAdj - netOfLines;

  // ── TB sort ───────────────────────────────────────────────────────────────

  const handleTbSort = (field: TBSortField) => {
    if (tbSortField === field) {
      setTbSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setTbSortField(field);
      setTbSortDirection('asc');
    }
  };

  // Entity pre-filter: restrict to the header's entity when in multi-entity mode
  const entityAccounts = useMemo(() => {
    const entityName = header.entityId
      ? entities.find(e => e.id === header.entityId)?.name
      : undefined;
    return entityName ? accounts.filter(a => a.entity === entityName) : accounts;
  }, [accounts, entities, header.entityId]);

  // Income Statement Only: restrict accounts to Income and Deductions FS Refs
  const incomeAccounts = useMemo(
    () => entityAccounts.filter((a) => {
      const ref = getFSRef(a.grouping);
      return ref === 'Income' || ref === 'Deductions';
    }),
    [entityAccounts]
  );


  const filteredAccounts = useMemo(() => {
    // M-Adjustment postings must always target Income Statement accounts (Income/Deductions),
    // regardless of which calcType template is used to drive the workpaper calculation.
    const pool = incomeAccounts;
    const q = tbSearch.toLowerCase().trim();
    const filtered = q
      ? pool.filter(
          (a) =>
            a.accountNumber.toLowerCase().includes(q) ||
            a.accountName.toLowerCase().includes(q)
        )
      : pool;

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (tbSortField) {
        case 'accountNumber':
          cmp = a.accountNumber.localeCompare(b.accountNumber, undefined, { numeric: true });
          break;
        case 'accountName':
          cmp = a.accountName.localeCompare(b.accountName);
          break;
        case 'grouping':
          cmp = (a.grouping || '').localeCompare(b.grouping || '');
          break;
        case 'subGrouping':
          cmp = (a.subGrouping || '').localeCompare(b.subGrouping || '');
          break;
      }
      return tbSortDirection === 'asc' ? cmp : -cmp;
    });
  }, [incomeAccounts, tbSearch, tbSortField, tbSortDirection]);

  // ── Signoff ───────────────────────────────────────────────────────────────

  const handleSign = useCallback(async (tier: SignoffTier) => {
    const signed = isTierSigned(tier, localHeader.signoffState);
    const cautionActive = hasCautionFlag(localHeader);

    if (signed && tier === 'Principal' && cautionActive) {
      const ts = Date.now();
      setLocalHeader((prev) => ({ ...prev, lastPrincipalReviewAt: ts }));
      try {
        await acknowledgePrincipalReview(header.id!);
      } catch (err) {
        console.error('Failed to re-sign:', err);
        setLocalHeader((prev) => ({ ...prev, lastPrincipalReviewAt: localHeader.lastPrincipalReviewAt }));
      }
      return;
    }
    if (signed) return;

    const next = computeNextSignoffState(localHeader.signoffState, tier);
    setLocalHeader((prev) => ({ ...prev, signoffState: next }));
    try {
      await updateSignoffState(header.id!, next);
    } catch (err) {
      console.error('Failed to update signoff:', err);
      setLocalHeader((prev) => ({ ...prev, signoffState: localHeader.signoffState }));
    }
  }, [header.id, localHeader]);

  const handleRemove = useCallback(async (tier: SignoffTier) => {
    if (!isTierClickable(tier, localHeader.signoffState)) return;
    const next = computeNextSignoffState(localHeader.signoffState, tier);
    setLocalHeader((prev) => ({ ...prev, signoffState: next }));
    try {
      await updateSignoffState(header.id!, next);
    } catch (err) {
      console.error('Failed to remove signoff:', err);
      setLocalHeader((prev) => ({ ...prev, signoffState: localHeader.signoffState }));
    }
  }, [header.id, localHeader.signoffState]);

  // ── Line manipulation ─────────────────────────────────────────────────────

  const addLine = useCallback((acc: TBAccount) => {
    setLines((prev) => [
      ...prev,
      {
        key: crypto.randomUUID(),
        accountId: acc.id!,
        accountNumber: acc.accountNumber,
        accountName: acc.accountName,
        debitCents: 0,
        creditCents: 0,
      },
    ]);
  }, []);

  const handleTbRowClick = useCallback((acc: TBAccount, e: React.MouseEvent) => {
    if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
      addLine(acc);
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      setFlashingTbAccountId(acc.id!);
      flashTimeoutRef.current = setTimeout(() => setFlashingTbAccountId(null), 500);
      setSelectedTbAccountIds(new Set());
      lastTbClickedIdRef.current = null;
      return;
    }

    if (e.shiftKey && lastTbClickedIdRef.current) {
      const ids = filteredAccounts.map((a) => a.id!);
      const lastIdx = ids.indexOf(lastTbClickedIdRef.current);
      const currIdx = ids.indexOf(acc.id!);
      if (lastIdx !== -1 && currIdx !== -1) {
        const [from, to] = lastIdx <= currIdx ? [lastIdx, currIdx] : [currIdx, lastIdx];
        setSelectedTbAccountIds(new Set(ids.slice(from, to + 1)));
        return;
      }
    }

    setSelectedTbAccountIds((prev) => {
      const next = new Set(prev);
      if (next.has(acc.id!)) next.delete(acc.id!);
      else next.add(acc.id!);
      return next;
    });
    lastTbClickedIdRef.current = acc.id!;
  }, [addLine, filteredAccounts]);

  const handleAddSelected = useCallback(() => {
    const accMap = new Map(accounts.map((a) => [a.id!, a]));
    selectedTbAccountIds.forEach((id) => {
      const acc = accMap.get(id);
      if (acc) addLine(acc);
    });
    setSelectedTbAccountIds(new Set());
    lastTbClickedIdRef.current = null;
  }, [accounts, selectedTbAccountIds, addLine]);

  const handleAccountModalSave = useCallback(async () => {
    if (!engagement.id) return;
    const updatedAccounts = await getAccounts(engagement.id);
    setAccounts(updatedAccounts);
    const newest = updatedAccounts.reduce<TBAccount | null>(
      (latest, acc) => (!latest || acc.createdAt > latest.createdAt ? acc : latest),
      null
    );
    if (newest) addLine(newest);
  }, [engagement.id, addLine]);

  const removeLine = useCallback((key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }, []);

  const updateDebit = useCallback((key: string, raw: string) => {
    const cents = inputValueToCents(raw);
    setLines((prev) =>
      prev.map((l) => l.key === key ? { ...l, debitCents: cents, creditCents: 0 } : l)
    );
  }, []);

  const updateCredit = useCallback((key: string, raw: string) => {
    const cents = inputValueToCents(raw);
    setLines((prev) =>
      prev.map((l) => l.key === key ? { ...l, creditCents: cents, debitCents: 0 } : l)
    );
  }, []);

  const handleSwap = useCallback(() => {
    setLines((prev) =>
      prev.map((l) => ({ ...l, debitCents: l.creditCents, creditCents: l.debitCents }))
    );
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!isBalanced || isSaving) return;
    setIsSaving(true);
    setSaveError(null);

    try {
      const newLines: Omit<JournalEntryLine, 'id'>[] = lines.map((l) => ({
        engagementId: engagement.id!,
        headerId: header.id!,
        accountId: l.accountId,
        type: 'M-ADJ' as const,
        amount: l.debitCents > 0 ? l.debitCents : -l.creditCents,
        createdAt: Date.now(),
      }));

      await saveMAdjLines(header, newLines, jeNumber.trim(), jeName.trim(), notesText.trim(), derivedNetAdj, differenceType, localHeader.calcType, calcInputs);
      onBack(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Shared classes ────────────────────────────────────────────────────────

  const thClass =
    'px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide bg-slate-100 dark:bg-neutral-800 text-left select-none whitespace-nowrap';

  const inputClass =
    'w-full px-2 py-1 text-sm font-mono text-right border border-gray-300 dark:border-neutral-600 rounded focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-neutral-800 text-slate-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-gray-600 transition-colors';

  // ── Net Adjustment status label ───────────────────────────────────────────

  const netAdjLabel =
    derivedNetAdj < 0 ? { text: 'Favorable', cls: 'text-green-600 dark:text-green-400' } :
    derivedNetAdj > 0 ? { text: 'Unfavorable', cls: 'text-red-600 dark:text-red-400' } :
    null;

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-taxtrail-bg dark:bg-neutral-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-taxtrail-bg dark:bg-neutral-900 transition-colors duration-300">

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-700 transition-colors duration-300">
        <div className="p-4 flex flex-wrap items-center gap-3">

          {/* Back */}
          <button
            onClick={() => onBack(false)}
            className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
            title="Back"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to List</span>
          </button>

          <div className="h-5 w-px bg-slate-200 dark:bg-neutral-700" />

          {/* Entity badge — shown in multi-entity mode */}
          {header.entityId && (() => {
            const name = entities.find(e => e.id === header.entityId)?.name;
            return name ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {name}
              </span>
            ) : null;
          })()}

          {/* Perm / Temp toggle */}
          <div className="flex rounded-full overflow-hidden border border-slate-200/60 dark:border-neutral-700/60 backdrop-blur-sm bg-white/60 dark:bg-neutral-800/60 text-xs font-semibold shadow-sm">
            {(['Permanent', 'Temporary'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setDifferenceType(type)}
                className={`px-2.5 py-0.5 transition-colors ${
                  differenceType === type
                    ? 'bg-purple-600 text-white'
                    : 'text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                }`}
                title={type === 'Permanent' ? 'Permanent Difference' : 'Temporary Difference'}
              >
                {type === 'Permanent' ? 'PERM' : 'TEMP'}
              </button>
            ))}
          </div>

          {/* Reference number */}
          <input
            type="text"
            value={jeNumber}
            onChange={(e) => setJeNumber(e.target.value)}
            className="w-28 px-2 py-1 text-sm font-mono font-semibold border border-transparent hover:border-slate-300 dark:hover:border-neutral-600 focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500 rounded bg-transparent dark:text-slate-100 transition-colors"
            placeholder="M-ADJ #"
            title="M-Adj Reference Number"
          />

          {/* Name */}
          <input
            type="text"
            value={jeName}
            onChange={(e) => setJeName(e.target.value)}
            className="flex-1 min-w-32 px-2 py-1 text-sm border border-transparent hover:border-slate-300 dark:hover:border-neutral-600 focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500 rounded bg-transparent text-slate-800 dark:text-slate-100 transition-colors"
            placeholder="Adjustment Name"
            title="M-Adj Name"
          />

          {/* Signoff */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400 dark:text-slate-500 mr-0.5">Signoff:</span>
            {(['Prepared', 'Principal', 'Final'] as SignoffTier[]).map((tier) => (
              <SignoffCheckbox
                key={tier}
                tier={tier}
                isSigned={isTierSigned(tier, localHeader.signoffState)}
                isClickable={isTierClickable(tier, localHeader.signoffState)}
                title={getSignoffTitle(tier, isTierSigned(tier, localHeader.signoffState), isTierClickable(tier, localHeader.signoffState), localHeader.signoffState, tier === 'Principal' ? hasCautionFlag(localHeader) : false)}
                hasCautionFlag={tier === 'Principal' ? hasCautionFlag(localHeader) : false}
                onSign={() => handleSign(tier)}
                onRemove={() => handleRemove(tier)}
              />
            ))}
          </div>

          {/* Save error */}
          {saveError && (
            <p className="text-xs text-red-600 dark:text-red-400">{saveError}</p>
          )}

          {/* Save button */}
          <Button
            variant="primary"
            size="sm"
            className="ml-auto"
            leftIcon={<Save className="w-4 h-4" />}
            onClick={handleSave}
            disabled={!isBalanced || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save M-Adjustment'}
          </Button>
        </div>
      </div>

      {/* ── DUAL-PANE BODY ─────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden p-4 gap-4">

        {/* ─── LEFT PANE ────────────────────────────────────────────────── */}
        <div className="flex flex-col w-1/2 glass-panel rounded-lg overflow-hidden">

          {/* ── Section 1: Calculation ───────────────────────────────────── */}
          {(() => {
            const ct = localHeader.calcType ?? 'Manual';
            const ci = calcInputs;

            const labelCls = 'text-xs text-slate-500 dark:text-slate-400';
            const valueCls = (cents: number) =>
              `text-sm font-mono text-right ${cents < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-100'}`;

            const InputRow = ({ label, fieldKey }: { label: string; fieldKey: string }) => (
              <div className="flex justify-between items-center py-1.5 gap-4">
                <span className={labelCls}>{label}</span>
                <div className="w-36">
                  <CurrencyInput
                    valueCents={ci[fieldKey] ?? 0}
                    onChange={(v) => setCalcInput(fieldKey, v)}
                    className="text-right"
                  />
                </div>
              </div>
            );

            const DisplayRow = ({ label, cents }: { label: string; cents: number }) => (
              <div className="flex justify-between items-center py-1.5">
                <span className={labelCls}>{label}</span>
                <span className={valueCls(cents)}>{displayAmountRounded(cents).text}</span>
              </div>
            );

            const TotalRow = () => (
              <div className="flex justify-between items-center py-2 mt-1 border-t border-slate-300 dark:border-neutral-600">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Net Adjustment</span>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-semibold font-mono ${derivedNetAdj < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-100'}`}>
                    {displayAmountRounded(derivedNetAdj).text}
                  </span>
                  {netAdjLabel && (
                    <span className={`text-xs font-semibold whitespace-nowrap ${netAdjLabel.cls}`}>
                      {netAdjLabel.text}
                    </span>
                  )}
                </div>
              </div>
            );

            return (
              <div className="bg-slate-50/50 dark:bg-neutral-800/50 p-6 border-b border-slate-200 dark:border-neutral-700 overflow-y-auto">
                <div className="w-full max-w-xs mx-auto">
                  {ct === 'Manual' && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                        Net Adjustment
                      </label>
                      <div className="flex items-center gap-3">
                        <CurrencyInput
                          valueCents={calcInputs.manualAmount || 0}
                          onChange={(v) => setCalcInput('manualAmount', v)}
                          className="flex-1"
                        />
                        {netAdjLabel && (
                          <span className={`text-xs font-semibold whitespace-nowrap ${netAdjLabel.cls}`}>
                            {netAdjLabel.text}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {ct === 'Standard' && (
                    <div>
                      <DisplayRow label="Book Amount (Auto)" cents={autoCYBalance} />
                      <InputRow label="Master Override" fieldKey="masterOverride" />
                      <TotalRow />
                    </div>
                  )}

                  {ct === 'Meals50' && (
                    <div>
                      <DisplayRow label="Book Amount (Auto)" cents={autoCYBalance} />
                      <DisplayRow label="50% Balance" cents={Math.round(autoCYBalance * -0.5)} />
                      <InputRow label="Master Override" fieldKey="masterOverride" />
                      <TotalRow />
                    </div>
                  )}

                  {ct === 'BookTaxDiff' && (
                    <div>
                      <DisplayRow label="Book Amount (Auto)" cents={autoCYBalance} />
                      <InputRow label="Tax Amount" fieldKey="taxAmount" />
                      <InputRow label="Master Override" fieldKey="masterOverride" />
                      <TotalRow />
                    </div>
                  )}

                  {ct === 'Rollforward' && (
                    <div>
                      <DisplayRow label="PY Balance (Auto)" cents={autoPYBalance} />
                      <InputRow label="PY Payout/Adj" fieldKey="pyPayout" />
                      <DisplayRow label="CY Balance (Auto)" cents={autoCYBalance} />
                      <InputRow label="CY Payout/Adj" fieldKey="cyPayout" />
                      <InputRow label="Master Override" fieldKey="masterOverride" />
                      <TotalRow />
                    </div>
                  )}

                  {ct === 'BSChange' && (
                    <div>
                      <InputRow label="Master Override" fieldKey="masterOverride" />
                      <TotalRow />
                    </div>
                  )}

                  {ct === 'IncomeStatement' && (
                    <div>
                      <DisplayRow label="Book Amount (Auto)" cents={autoCYBalance} />
                      <InputRow label="Tax Amount" fieldKey="taxAmount" />
                      {/depreciation/i.test(jeName) && (ci.taxAmount ?? 0) > 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 text-right pr-1 -mt-1 pb-1">
                          Tip: Enter Tax Depreciation as a negative value to create a Favorable adjustment.
                        </p>
                      )}
                      <InputRow label="Master Override" fieldKey="masterOverride" />
                      <TotalRow />
                    </div>
                  )}

                  {(ct === 'BS_Liability' || ct === 'BS_Asset') && (
                    <div>
                      <DisplayRow label="PY Balance (Auto)" cents={autoPYBalance} />
                      <DisplayRow label="CY Balance (Auto)" cents={autoCYBalance} />
                      <InputRow label="Master Override" fieldKey="masterOverride" />
                      <TotalRow />
                    </div>
                  )}

                  {(ct === 'BS_Liability_Payout' || ct === 'BS_Asset_Payout') && (
                    <div>
                      <DisplayRow label="PY Balance (Auto)" cents={autoPYBalance} />
                      <InputRow label="PY Payout" fieldKey="pyPayout" />
                      <DisplayRow label="CY Balance (Auto)" cents={autoCYBalance} />
                      <InputRow label="CY Payout" fieldKey="cyPayout" />
                      <InputRow label="Master Override" fieldKey="masterOverride" />
                      <TotalRow />
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Section 2: Accounts ──────────────────────────────────────── */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <>
              {/* Search + New Account */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-neutral-700 bg-white/50 dark:bg-neutral-900/50">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={tbSearch}
                    onChange={(e) => setTbSearch(e.target.value)}
                    placeholder="Search account # or name…"
                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-neutral-800 text-slate-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                  />
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setIsAccountModalOpen(true)}
                >
                  + NEW ACCOUNT
                </Button>
              </div>

              {/* Multi-select action bar */}
              {selectedTbAccountIds.size > 1 && (
                <div className="flex items-center justify-between px-3 py-2 border-b border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30">
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                    {selectedTbAccountIds.size} selected
                  </span>
                  <button
                    onClick={handleAddSelected}
                    className="text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 dark:bg-blue-700 dark:hover:bg-blue-600 px-3 py-1 rounded-md shadow-sm transition-colors"
                  >
                    ADD {selectedTbAccountIds.size} ACCOUNTS →
                  </button>
                </div>
              )}

              {/* TB Table */}
              <div className="flex-1 overflow-auto" style={{ scrollbarGutter: 'auto' }}>
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th onClick={() => handleTbSort('accountNumber')} className={`${thClass} w-24 cursor-pointer hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors`}>
                        Acct #<TbSortIndicator field="accountNumber" current={tbSortField} direction={tbSortDirection} />
                      </th>
                      <th onClick={() => handleTbSort('accountName')} className={`${thClass} cursor-pointer hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors`}>
                        Name<TbSortIndicator field="accountName" current={tbSortField} direction={tbSortDirection} />
                      </th>
                      <th onClick={() => handleTbSort('grouping')} className={`${thClass} hidden lg:table-cell cursor-pointer hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors`}>
                        Grouping<TbSortIndicator field="grouping" current={tbSortField} direction={tbSortDirection} />
                      </th>
                      <th onClick={() => handleTbSort('subGrouping')} className={`${thClass} hidden xl:table-cell cursor-pointer hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors`}>
                        Sub-Group<TbSortIndicator field="subGrouping" current={tbSortField} direction={tbSortDirection} />
                      </th>
                      <th className={`${thClass} text-right`}>Book</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAccounts.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-12 text-slate-400 dark:text-slate-600 text-xs">
                          {tbSearch ? `No accounts match "${tbSearch}"` : 'No accounts in this engagement.'}
                        </td>
                      </tr>
                    ) : (
                      filteredAccounts.map((acc) => {
                        const book = displayAmountRounded(acc.bookBalance);
                        const isSelected = selectedTbAccountIds.has(acc.id!);
                        const isFlashing = flashingTbAccountId === acc.id;
                        return (
                          <tr
                            key={acc.id}
                            onClick={(e) => handleTbRowClick(acc, e)}
                            onDoubleClick={() => addLine(acc)}
                            onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                            className={`border-b border-slate-100 dark:border-neutral-800 cursor-pointer transition-colors duration-300 ${
                              isFlashing
                                ? 'bg-blue-100 dark:bg-blue-900/40'
                                : isSelected
                                  ? 'bg-blue-50 dark:bg-blue-950/40'
                                  : 'hover:bg-slate-50/80 dark:hover:bg-neutral-800/60'
                            }`}
                          >
                            <td className="px-3 py-2 font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">
                              {acc.accountNumber}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-800 dark:text-slate-100 max-w-[140px] truncate" title={acc.accountName}>
                              {acc.accountName}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 hidden lg:table-cell max-w-[100px] truncate" title={acc.grouping}>
                              {acc.grouping || '—'}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 hidden xl:table-cell max-w-[80px] truncate" title={acc.subGrouping}>
                              {acc.subGrouping || '—'}
                            </td>
                            <td className={`px-3 py-2 text-xs font-mono text-right whitespace-nowrap ${book.isNegative ? 'text-taxtrail-red' : 'text-slate-700 dark:text-slate-300'}`}>
                              {book.text}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          </div>
        </div>

        {/* ─── RIGHT PANE: LINES ────────────────────────────────────────── */}
        <div className="flex flex-col w-1/2 glass-panel rounded-lg overflow-hidden">
          {/* Pane header */}
          <div className="flex items-center justify-end gap-3 px-3 py-2 border-b border-slate-200 dark:border-neutral-700 bg-white/50 dark:bg-neutral-900/50">
            <button
              onClick={handleSwap}
              title="Swap all Debits and Credits"
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-neutral-600 rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors"
            >
              <ArrowLeftRight className="w-3 h-3" />
              Swap Dr/Cr
            </button>
            {lines.length === 0 ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-neutral-800 dark:text-slate-400">
                Empty
              </span>
            ) : isBalanced ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Balanced
              </span>
            ) : (
              <>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400">
                  <XCircle className="w-3.5 h-3.5" />
                  Unbalanced
                </span>
                <span className="text-xs text-red-500 dark:text-red-400 font-mono">
                  Δ {displayAmountRounded(Math.abs(balanceDiff)).text}
                </span>
              </>
            )}
          </div>

          {/* Lines table */}
          <div className="flex-1 overflow-auto" style={{ scrollbarGutter: 'auto' }}>
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className={`${thClass} w-24`}>Acct #</th>
                  <th className={thClass}>Name</th>
                  <th className={`${thClass} w-28 text-right`}>Debit</th>
                  <th className={`${thClass} w-28 text-right`}>Credit</th>
                  <th className={`${thClass} w-10 text-center`}></th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16 text-slate-400 dark:text-slate-600 text-xs">
                      <Plus className="w-8 h-8 mx-auto mb-2 text-slate-200 dark:text-slate-800" />
                      Click a row in the accounts list on the left to add it
                    </td>
                  </tr>
                ) : (
                  lines.map((line) => (
                    <tr
                      key={line.key}
                      className={`border-b border-slate-100 dark:border-neutral-800 transition-colors ${
                        line.isInvalidAccount
                          ? 'bg-red-50/50 dark:bg-red-900/10'
                          : 'hover:bg-slate-50 dark:hover:bg-neutral-800/60'
                      }`}
                    >
                      <td className={`px-3 py-1.5 font-mono text-xs whitespace-nowrap ${line.isInvalidAccount ? 'text-taxtrail-red' : 'text-slate-700 dark:text-slate-300'}`}>
                        {line.accountNumber}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-xs max-w-[120px] truncate ${line.isInvalidAccount ? 'text-taxtrail-red' : 'text-slate-800 dark:text-slate-100'}`}
                        title={line.isInvalidAccount ? 'M-Adjustments must post to Income Statement accounts (Income/Deductions). This account is not valid for posting.' : line.accountName}
                      >
                        {line.accountName}
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={focusedInputKey === `${line.key}-dr` && line.debitCents === 0 ? '' : centsToInputValue(line.debitCents)}
                          onChange={(e) => updateDebit(line.key, e.target.value)}
                          onFocus={(e) => { setFocusedInputKey(`${line.key}-dr`); e.target.select(); }}
                          onBlur={() => setFocusedInputKey(null)}
                          className={inputClass}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={focusedInputKey === `${line.key}-cr` && line.creditCents === 0 ? '' : centsToInputValue(line.creditCents)}
                          onChange={(e) => updateCredit(line.key, e.target.value)}
                          onFocus={(e) => { setFocusedInputKey(`${line.key}-cr`); e.target.select(); }}
                          onBlur={() => setFocusedInputKey(null)}
                          className={inputClass}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => removeLine(line.key)}
                          title="Remove line"
                          className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── NOTES ─────────────────────────────────────────────────── */}
          <div className="border-t border-slate-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={() => setIsNotesExpanded(v => !v)}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors select-none"
            >
              Notes
              {isNotesExpanded
                ? <ChevronDown className="w-4 h-4" />
                : <ChevronUp className="w-4 h-4" />}
            </button>
            {isNotesExpanded && (
              <div className="px-4 pb-3">
                <textarea
                  value={notesText}
                  onChange={e => setNotesText(e.target.value)}
                  rows={3}
                  placeholder="Add reviewer notes…"
                  className="w-full resize-none overflow-y-auto rounded-md border border-gray-200 dark:border-neutral-600 bg-white/60 dark:bg-neutral-800/60 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sage-400 dark:focus:ring-sage-600 transition-colors"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── NEW ACCOUNT MODAL ──────────────────────────────────────────────── */}
      <AccountModal
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        onSave={handleAccountModalSave}
        engagement={engagement}
      />
    </div>
  );
}
